// =============================================================================
// buildVoiceClonePayload — frontend → backend bridge for Voice-Clone sends.
//
// Used by the AI-mode entry points (NormalMode, BulkMode, AutoSendMode,
// InboxMode) to opt-in to the Voice-Clone backend pipeline.
//
// Why a separate helper?
//   - Standard-mode payloads stay byte-identical to before (zero regression).
//   - Voice-Clone-mode templates need a different, smaller payload: the
//     backend loads the clone + template fresh from RTDB, so we only need
//     identifiers + per-call recipient/sender data.
//   - One helper means all four AI modes share the same dispatch logic — no
//     drift between them.
//
// Usage pattern at the call-site:
//
//   import { isVoiceCloneTopicData, buildVoiceClonePayload } from
//     '../CloneTemplate/buildVoiceClonePayload';
//
//   const requestData = isVoiceCloneTopicData(topicData)
//     ? buildVoiceClonePayload({ topicData, templateId, contact, sender })
//     : standardRequestData;   // existing logic — unchanged
//
//   fetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), { body: JSON.stringify(requestData) })
// =============================================================================

import { doc, getDoc } from "firebase/firestore";
import { TEMPLATE_MODES } from "./cloneTemplateUtils";
import { firestore } from "../../firebaseConfig";
import { connectorsForPrompt } from "../../shared/connectorContext";

/**
 * Load the FRESH contact document straight from Firestore right before a
 * Voice-Clone send.
 *
 * Why this exists: the World-table row that flows into the send (rowData) is a
 * SNAPSHOT frozen when the email popup / bulk run started. Several research
 * tools (Instagram, SEO, Bulk research) write straight to Firestore via
 * updateDoc WITHOUT updating the in-memory row, so the frozen snapshot can be
 * missing `researchIntelligence` / `websiteAnalysis` that already exist on the
 * server. Re-reading the doc here guarantees the generator gets the latest
 * research — fixing the "research arrives empty even though I ran it" bug.
 *
 * Fail-safe: on any error (offline, permission, missing id) it returns the
 * original snapshot so a send is never blocked.
 *
 * Merge order: server data wins on overlapping fields (research is server-
 * authored), while client-only attachments on the snapshot (selectedTopic,
 * templateOverride, …) are preserved because they don't exist in the doc.
 */
export const loadFreshContactRow = async (userId, rowData) => {
  const id = rowData?.id;
  if (!userId || !id) return rowData;
  try {
    const snap = await getDoc(doc(firestore, `World/${userId}/db/${id}`));
    if (snap.exists()) {
      return { ...rowData, ...snap.data(), id: snap.id };
    }
  } catch (err) {
    console.warn("[VoiceClone] fresh contact fetch failed, using snapshot:", err?.message);
  }
  return rowData;
};

/**
 * Cheap mode check on the raw topic data straight out of RTDB. Both the
 * standard and voice-clone templates live under the same /customTopics path
 * with the same `mode` discriminator.
 */
export const isVoiceCloneTopicData = (topicData) =>
  topicData?.mode === TEMPLATE_MODES.VOICE_CLONE;

/**
 * Single source of truth for "should the backend generate a smart subject
 * for this voice-clone template?". Mirrors the editor's mutually-exclusive
 * UI semantic (Smart Subject toggle hides the static list — and vice versa),
 * so the four AI modes (Normal / Bulk / Inbox / Preview) and the queue
 * workers (Outreach, FlowAutomation) all behave identically.
 *
 *   cloneConfig.smartSubject === true  → ask backend (default)
 *   cloneConfig.smartSubject === false → skip backend, caller picks from the
 *                                        static `subjectLines` list instead
 *
 * Defaults to `true` for back-compat with templates persisted before the
 * toggle was introduced. The static-list fallback path (when smart fails)
 * is the caller's responsibility — this helper only governs whether the
 * backend is asked to gen a subject in the first place.
 */
export const shouldGenerateSmartSubject = (topicData) => {
  if (!isVoiceCloneTopicData(topicData)) return false;
  return topicData?.cloneConfig?.smartSubject !== false;
};

/**
 * Stats bucket id for a smart subject. We segment by variantId so the same
 * template can A/B-test multiple structures and see which one's subject
 * style performs best — instead of every smart subject landing in a single
 * "voice_clone_smart" bucket that hides the difference.
 *
 * Falls back to the un-suffixed legacy id when no variant is known so
 * historic stats remain queryable.
 */
export const buildVoiceCloneSmartSubjectId = (variantId) => {
  // RTDB keys disallow ".", "#", "$", "[", "]", "/" — sanitize defensively
  // so an exotic variant id can't poison the stats path. Empty/unknown
  // variantId falls back to the legacy un-suffixed key for back-compat.
  const v = variantId
    ? String(variantId).trim().replace(/[.#$/[\]]/g, "_")
    : "";
  return v ? `voice_clone_smart__${v}` : "voice_clone_smart";
};

/**
 * Strip a row coming from the World table down to the recipient fields the
 * backend prompt cares about — including the rich pre-fetched research data
 * that the BonusClub research tools have already written into the World DB.
 *
 * IMPORTANT — recipient resolution:
 *   Decision-maker info is stored in `rowData.lead_decision_makers` as a JSON
 *   string of an array. The standard-mode pipeline uses
 *   `getRecipientInfoForContact(rowData, selectedEmail)` to resolve the right
 *   DM name + role for the email currently being sent to. We accept that
 *   resolved object here as `recipient` so we don't duplicate the resolution
 *   logic — the caller already has it.
 *
 * IMPORTANT — research data:
 *   The BonusClub research tools (Instagram-Insight, Website-Analysis, SEO,
 *   Outreach-Notes) populate `rowData.researchIntelligence`, `rowData.websiteAnalysis`
 *   and `rowData.lead_notes` BEFORE generation. We forward that pre-fetched
 *   data so the Voice-Clone prompt can use it just like the Standard-mode
 *   prompt does (NormalModeEmail.js:228-321 builds an equivalent context
 *   string for standard mode). We do NOT trigger any live web fetch here —
 *   that's the research tools' job, run separately.
 *
 * The backend's enabledStandardKeys / enabledCustomIds filtering happens at
 * generation time on the SERVER (it owns the template) — we hand it the raw
 * data here and let the prompt builder filter.
 *
 * @param {object} rowData            World-table row for the contact
 * @param {object} [recipient]        Resolved recipient: { name, role } — from getRecipientInfoForContact
 * @param {object} [opts]
 * @param {string} [opts.firstName]   Recipient first name (already resolved by caller)
 * @param {Array}  [opts.customFieldsData] Admin custom-field values for this row
 */
export const buildContactBlock = (rowData = {}, recipient = null, opts = {}) => {
  const { firstName, customFieldsData } = opts;

  // Outreach notes — only the human-written outreach context, not internal
  // notes / research dumps that already live in the structured fields.
  const outreachNotes = Array.isArray(rowData.lead_notes)
    ? rowData.lead_notes
        .filter((n) => n?.type === "outreach" && n?.text)
        .map((n) => n.text)
        .join("\n")
    : "";

  // researchIntelligence is the structured artifact our research tools write.
  // We forward only the parts that materially influence the prompt; raw post
  // arrays are trimmed to the most recent 3 to keep the prompt tight.
  const ri = rowData.researchIntelligence || {};
  const igPosts = Array.isArray(ri.instagramPosts)
    ? ri.instagramPosts
        .filter((p) => p?.text)
        .slice(0, 3)
        .map((p) => ({
          text:         String(p.text).slice(0, 220),
          likeCount:    p.like_count ?? null,
          commentCount: p.comment_count ?? null,
        }))
    : [];

  return {
    // ---- Core identity fields ----
    businessName:        rowData.business_name        || "",
    website:             rowData.lead_website         || "",
    recipientName:       recipient?.name              || "",
    recipientRole:       recipient?.role              || "",
    firstName:           firstName                    || rowData.first_name || rowData.firstName || "",
    city:                rowData.lead_city            || "",
    country:             rowData.lead_country         || "",
    description:         rowData.lead_description     || "",

    // ---- Extended World-DB fields (mapped 1:1 to STANDARD_PERSONALIZATION_FIELDS) ----
    email:               rowData.lead_email           || "",
    phone:               rowData.lead_telephone       || "",
    decisionMakers:      rowData.lead_decision_makers || "",
    socials:             rowData.lead_socials         || "",
    instagramFollowers:  rowData.lead_instagram_followers || "",
    rating:              rowData.lead_rating          || "",
    reviews:             rowData.lead_reviews         || "",

    // ---- Pre-fetched research (written by separate research tools) ----
    research: {
      instagramInsight: ri.instagramInsight || null,
      instagramPosts:   igPosts,
      seoAnalysis:      ri.seoAnalysis      || null,
      websiteAnalysis:  rowData.websiteAnalysis || null,
      outreachNotes:    outreachNotes        || "",
      // Web Research (Spy) intelligence — the richest signal we have. It used
      // to be silently dropped because the payload only forwarded IG/SEO/
      // Website. These are the recipient-side facts/hooks the writer (and the
      // backend Relevance Brief) need for genuine personalization.
      webResearch: ri.version
        ? {
            companyInfo:     typeof ri.companyInfo === "string" ? ri.companyInfo : "",
            keyFacts:        Array.isArray(ri.keyFacts)   ? ri.keyFacts.slice(0, 8)   : [],
            buySignals:      Array.isArray(ri.buySignals) ? ri.buySignals.slice(0, 6) : [],
            latestNews:      Array.isArray(ri.latestNews) ? ri.latestNews.slice(0, 5) : [],
            quickStats:      ri.quickStats && typeof ri.quickStats === "object" ? ri.quickStats : null,
            shopIntelligence: ri.shopIntelligence && typeof ri.shopIntelligence === "object" ? ri.shopIntelligence : null,
          }
        : null,
      // Google Review Audit — the backend trims and renders it with a tact rule
      // (functions/helpers/reviewAuditContext.js). Raw review texts stay out.
      googleReviews: ri.googleReviewAudit?.place
        ? {
            place:    ri.googleReviewAudit.place,
            stats:    ri.googleReviewAudit.stats || null,
            analysis: ri.googleReviewAudit.analysis || null,
          }
        : null,
      externalData: connectorsForPrompt(ri),
    },

    // ---- Admin-defined custom fields ----
    customFields:  Array.isArray(customFieldsData) ? customFieldsData : [],
  };
};

/**
 * Condense a voice-clone template's campaign brief (which lives entirely in
 * `cloneConfig`, NOT in the standard `generalInfo`/`topicSpecificNote` fields)
 * into a single plain-text block. The SalesGod Writer reads `generalInfo` +
 * `templateNote` as its "sender offer" — so without this, clone templates feed
 * the Writer an EMPTY offer and the suggestions can't reflect the campaign.
 *
 * Returns '' when there's nothing useful, so the caller can `|| ''` safely.
 */
export const buildCloneWriterBrief = (cloneConfig = {}) => {
  if (!cloneConfig || typeof cloneConfig !== "object") return "";
  const parts = [];
  const add = (label, val) => {
    const v = (val == null ? "" : String(val)).trim();
    if (v) parts.push(`${label}: ${v}`);
  };

  add("What makes us special", cloneConfig.uniqueAdvantage);
  add("Audience pain points", cloneConfig.painPoint);
  add("Goal of these emails", cloneConfig.goal);
  add("References to use", cloneConfig.references);
  add("Extra rules", cloneConfig.extraRules);
  // Legacy fields some older templates still carry.
  add("Offer", cloneConfig.offer);
  add("Audience", cloneConfig.audience);

  if (Array.isArray(cloneConfig.fixedFacts) && cloneConfig.fixedFacts.length) {
    const facts = cloneConfig.fixedFacts
      .map((f) => (typeof f === "string" ? f : f?.text || f?.value || ""))
      .filter(Boolean);
    if (facts.length) parts.push(`Fixed facts (only use these, never invent numbers): ${facts.join("; ")}`);
  }

  if (Array.isArray(cloneConfig.angles) && cloneConfig.angles.length) {
    const angles = cloneConfig.angles
      .map((a) => (typeof a === "string" ? a : a?.text || a?.label || ""))
      .filter(Boolean);
    if (angles.length) parts.push(`Preferred angles: ${angles.join("; ")}`);
  }

  if (Array.isArray(cloneConfig.infoAddons) && cloneConfig.infoAddons.length) {
    cloneConfig.infoAddons.forEach((pool) => {
      const items = Array.isArray(pool?.items) ? pool.items.filter(Boolean) : [];
      if (items.length) parts.push(`${pool.label || "Info"}: ${items.join("; ")}`);
    });
  }

  return parts.join("\n");
};

/**
 * Build the sender block from user settings. Same shape as standard-mode
 * fields but namespaced under `sender` so the backend can route it to the
 * Voice-Clone prompt builder cleanly.
 */
export const buildSenderBlock = (userSettings = {}, extras = {}) => {
  const senderName = userSettings.user_name || "";
  // Best-effort first name extraction so the sign-off can use it without
  // requiring a separate "first name" field in user settings.
  const firstName = senderName ? senderName.trim().split(/\s+/)[0] : "";
  return {
    senderName,
    firstName,
    senderEmail: userSettings.user_email || "",
    senderRole:  userSettings.user_role  || "",
    textSignature: extras.textSignature || "",
  };
};

/**
 * Final payload to POST to worldEmailCreator. The backend dispatches on
 * `mode === 'voiceClone'` and routes to the Voice-Clone pipeline; standard
 * requests keep the original shape and code path.
 *
 * Required:
 *   topicData    — the template object (must have mode='voiceClone' and cloneConfig.cloneId)
 *   templateId   — RTDB key under /customTopics
 *   rowData      — World-table row for the recipient
 *   userSettings — current user settings (for sender block)
 *
 * Optional:
 *   recipient         — { name, role } from getRecipientInfoForContact
 *   firstName         — recipient first name (already resolved by caller)
 *   variantId         — pin a specific variant (e.g. for live preview)
 *   customFieldsData  — admin custom fields with values for this contact
 *   includeSubject    — set false to skip subject generation
 */
export const buildVoiceClonePayload = ({
  topicData,
  templateId,
  rowData,
  userSettings,
  userId,
  recipient,
  firstName,
  variantId,
  customFieldsData,
  includeSubject = true,
  // One-off CopyTwin switch: lets a Re-Generate use an alternative trained
  // clone for a single send WITHOUT mutating the template's saved cloneId.
  // Falls back to the template's configured clone when not provided.
  cloneIdOverride,
  textSignature,
}) => {
  const cloneId = cloneIdOverride || topicData?.cloneConfig?.cloneId;
  if (!cloneId) {
    // Defensive: don't construct an invalid payload — the caller should fall
    // back to standard mode (or surface a clear UX error). Returning null
    // makes that a single-line check at the call site.
    return null;
  }

  return {
    mode: "voiceClone",
    // Pass the active workspace userId so the backend can resolve the right
    // RTDB path when the caller is operating inside a switched team. Without
    // this, resolveTeamUserId falls back to the caller's own uid which can
    // point at the wrong workspace's templates / clones.
    ...(userId ? { userId } : {}),
    templateId,
    cloneId,
    ...(variantId ? { variantId } : {}),
    // Lets the backend load this contact's call notes when the template uses them.
    ...(rowData?.id ? { contactId: rowData.id } : {}),
    contact: buildContactBlock(rowData, recipient, { firstName, customFieldsData }),
    sender:  buildSenderBlock(userSettings, { textSignature }),
    includeSubject,
  };
};

/**
 * Response shape returned by the backend when mode='voiceClone'. Mapped to
 * fields the existing frontend already consumes — no new state fields needed
 * for the basic flow.
 *
 *   emailContent   → setBodyContent(...)
 *   subjectLine    → setSubjectLine(...)
 *   variantId      → trackingData.variantId  (for per-variant reply rates)
 *   structureId    → optional analytics tag
 *   modelUsed      → optional analytics tag
 */
export const VOICE_CLONE_RESPONSE_KEYS = [
  "emailContent",
  "subjectLine",
  "variantId",
  "variantLabel",
  "structureId",
  "lengthTweakId",
  "qualityTier",
  "modelUsed",
  "language",
  "targetWords",
];
